/**
 * Application Constants
 *
 * Centralized constants for the application
 * Keep in sync with backend constants
 */

// =============================================================================
// API Routes
// =============================================================================

export const API_ROUTES = {
  // Auth
  AUTH: {
    LOGIN: '/auth/login',
    PATIENT_LOGIN: '/auth/patient-login',
    // El paciente cambia el portal a otra de sus entidades. No vuelve a
    // pedirle los datos: ya los demostró al entrar.
    PATIENT_SWITCH: '/auth/patient-switch',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    ME: '/auth/me',
    // 2FA
    TWO_FACTOR: {
      STATUS: '/auth/2fa/status',
      SETUP: '/auth/2fa/setup',
      VERIFY: '/auth/2fa/verify',
      DISABLE: '/auth/2fa/disable',
      VALIDATE: '/auth/2fa/validate',
      COMPLETE_LOGIN: '/auth/2fa/complete-login',
    },
  },

  // Patients
  PATIENTS: {
    BASE: '/patients',
    BY_ID: (id: string) => `/patients/${id}`,
    UPDATE: (id: string) => `/patients/${id}/update`,
    DELETE: (id: string) => `/patients/${id}/delete`,
    STUDIES: (id: string) => `/patients/${id}/studies`,
    SEARCH: '/patients/search',
  },

  // Studies
  STUDIES: {
    BASE: '/studies',
    BATCH: '/studies/batch',
    BY_ID: (id: string) => `/studies/${id}`,
    UPDATE: (id: string) => `/studies/${id}/update`,
    DELETE: (id: string) => `/studies/${id}/delete`,
    STATUS: (id: string) => `/studies/${id}/status`,
    DOWNLOAD: (id: string) => `/studies/${id}/download`,
    FILE: (id: string) => `/studies/${id}/file`,
    INSTANCES: (id: string) => `/studies/${id}/instances`,
    AMEND_INFO: (id: string) => `/studies/${id}/amend-info`,
    AMEND: (id: string) => `/studies/${id}/amend`,
  },

  // Users
  USERS: {
    BASE: '/users',
    BY_ID: (id: string) => `/users/${id}`,
    RADIOLOGISTS: '/users/radiologists',
    UPDATE: (id: string) => `/users/${id}/update`,
    DELETE: (id: string) => `/users/${id}/delete`,
    RESET_PASSWORD: (id: string) => `/users/${id}/reset-password`,
    ACTIVATE: (id: string) => `/users/${id}/activate`,
    DEACTIVATE: (id: string) => `/users/${id}/deactivate`,
    // Profile (current user)
    ME: '/users/me',
    ME_UPDATE: '/users/me/update',
    CHANGE_PASSWORD: '/users/me/change-password',
  },

  // Institutions (SUPER_ADMIN only)
  INSTITUTIONS: {
    BASE: '/institutions',
    BY_ID: (id: string) => `/institutions/${id}`,
    UPDATE: (id: string) => `/institutions/${id}`,
    DELETE: (id: string) => `/institutions/${id}`,
    ACTIVATE: (id: string) => `/institutions/${id}/activate`,
    DEACTIVATE: (id: string) => `/institutions/${id}/deactivate`,
    STATS: (id: string) => `/institutions/${id}/stats`,
    GLOBAL_STATS: '/institutions/stats/global',
    ONBOARD: (id: string) => `/institutions/${id}/onboard`,
    CATALOG_GLOBAL: '/institutions/catalog/global',
    SUBSCRIPTION: (id: string) => `/institutions/${id}/subscription`,
    PAYMENTS: (id: string) => `/institutions/${id}/payments`,
    USAGE: (id: string) => `/institutions/${id}/usage`,
    STUDY_TYPES: (id: string) => `/institutions/${id}/study-types`,
    STUDY_TYPE_DETAIL: (id: string, resourceId: string) =>
      `/institutions/${id}/study-types/${resourceId}`,
    PRIORITIES: (id: string) => `/institutions/${id}/priorities`,
    PRIORITY_DETAIL: (id: string, resourceId: string) =>
      `/institutions/${id}/priorities/${resourceId}`,
  },

  // Plans & Subscriptions (SUPER_ADMIN only)
  PLANS: {
    BASE: '/plans',
    BY_ID: (id: string) => `/plans/${id}`,
    PRICING: (id: string) => `/plans/${id}/pricing`,
  },
  SUBSCRIPTIONS: {
    SUSPEND: (id: string) => `/subscriptions/${id}/suspend`,
    REACTIVATE: (id: string) => `/subscriptions/${id}/reactivate`,
    EXTEND_TRIAL: (id: string) => `/subscriptions/${id}/extend-trial`,
  },

  // Admin Dashboard KPIs (SUPER_ADMIN only)
  ADMIN_DASHBOARD: {
    BUSINESS_KPIS: '/admin/dashboard/business-kpis',
    RECENT_ACTIVITY: '/admin/dashboard/recent-activity',
    INSTITUTIONS_SUMMARY: '/admin/dashboard/institutions-summary',
  },

  // Global Catalog (SUPER_ADMIN only) — institution_id IS NULL
  GLOBAL_CATALOG: {
    STUDY_TYPES: '/admin/catalog/study-types',
    STUDY_TYPE_DETAIL: (id: string) => `/admin/catalog/study-types/${id}`,
    PRIORITIES: '/admin/catalog/priorities',
    PRIORITY_DETAIL: (id: string) => `/admin/catalog/priorities/${id}`,
  },

  // Admin Audit (SUPER_ADMIN only) — supervision profesional cross-tenant
  ADMIN_AUDIT: {
    SEARCH: '/admin/audit/search',
    SECURITY_STATS: '/admin/audit/security-stats',
    EXPORT: '/admin/audit/export',
  },

  // Admin Provisioning (SUPER_ADMIN only) — onboarding integral atomico
  ADMIN_PROVISIONING: {
    PROVISION: '/admin/institutions/provision',
    PROVISION_DEMO: '/admin/institutions/provision-demo',
  },

  // Dictado por voz — transcribe el servidor de la clinica, no un tercero
  DICTATION: {
    AVAILABILITY: '/dictation/availability',
    TRANSCRIBE: '/dictation/transcribe',
  },

  // Worklist
  WORKLIST: {
    BASE: '/worklist',
    BY_ID: (id: string) => `/worklist/${id}`,
    UPDATE: (id: string) => `/worklist/${id}/update`,
    DELETE: (id: string) => `/worklist/${id}/delete`,
    MY: '/worklist/my',
    OVERDUE: '/worklist/overdue',
    UNASSIGNED: '/worklist/unassigned',
    RETURNED: '/worklist/returned',
    ASSIGN: (id: string) => `/worklist/${id}/assign`,
    START: (id: string) => `/worklist/${id}/start`,
    COMPLETE: (id: string) => `/worklist/${id}/complete`,
    CANCEL: (id: string) => `/worklist/${id}/cancel`,
    RETURN: (id: string) => `/worklist/${id}/return`,
  },

  // Reports
  REPORTS: {
    BASE: '/reports',
    BY_ID: (id: string) => `/reports/${id}`,
    UPDATE: (id: string) => `/reports/${id}/update`,
    DELETE: (id: string) => `/reports/${id}/delete`,
    STATUS: (id: string) => `/reports/${id}/status`,
    SIGN: (id: string) => `/reports/${id}/sign`,
    AMEND: (id: string) => `/reports/${id}/amend`,
    // Addendum: agrega una nota SIN tocar el informe. La enmienda de arriba
    // reescribe y emite una version nueva. Son dos actos distintos.
    ADDENDUM: (id: string) => `/reports/${id}/addendum`,
    ADDENDA: (id: string) => `/reports/${id}/addenda`,
    PDF: (id: string) => `/reports/${id}/pdf`,
    VERIFY: (id: string) => `/reports/${id}/verify`,
    // Transcription workflow
    TRANSCRIPTION_QUEUE: '/reports/transcription-queue',
    AWAITING_REVIEW: '/reports/awaiting-review',
    START_TRANSCRIPTION: (id: string) => `/reports/${id}/start-transcription`,
    SUBMIT_REVIEW: (id: string) => `/reports/${id}/submit-review`,
  },

  // Audit
  AUDIT: {
    BASE: '/audit',
    RESOURCE: (type: string, id: string) => `/audit/resource/${type}/${id}`,
    USER: (userId: string) => `/audit/user/${userId}`,
    SECURITY_ALERTS: '/audit/security-alerts',
  },

  // Patient Portal (for patients only)
  PATIENT_PORTAL: {
    ME: '/patient-portal/me',
    STUDIES: '/patient-portal/studies',
    STUDY_BY_ID: (id: string) => `/patient-portal/studies/${id}`,
    STUDY_REPORT: (id: string) => `/patient-portal/studies/${id}/report`,
    STUDY_ATTACHMENTS: (id: string) => `/patient-portal/studies/${id}/attachments`,
    ATTACHMENT_DOWNLOAD: (studyId: string, attachmentId: string) =>
      `/patient-portal/studies/${studyId}/attachments/${attachmentId}/download`,
    // Descarga del examen completo. El paciente se lleva todas sus imágenes
    // en un archivo, no una por una: un estudio son cientos de cortes.
    STUDY_ARCHIVE: (id: string) => `/patient-portal/studies/${id}/archivo`,
    STUDY_INSTANCES: (id: string) => `/patient-portal/studies/${id}/instances`,
    // Archivo DICOM original: lo que lee el visor y lo que el paciente puede
    // llevarse a una segunda opinión.
    INSTANCE_FILE: (studyId: string, instanceId: string) =>
      `/patient-portal/studies/${studyId}/instances/${instanceId}/file`,
    INSTANCE_IMAGE: (studyId: string, instanceId: string) =>
      `/patient-portal/studies/${studyId}/instances/${instanceId}/image`,
    INSTANCE_THUMBNAIL: (studyId: string, instanceId: string) =>
      `/patient-portal/studies/${studyId}/instances/${instanceId}/thumbnail`,
  },

  // Study Types (Tipos de Estudio)
  STUDY_TYPES: {
    BASE: '/study-types',
    BY_ID: (id: string) => `/study-types/${id}`,
    UPDATE: (id: string) => `/study-types/${id}/update`,
    DELETE: (id: string) => `/study-types/${id}/delete`,
    ACTIVE: '/study-types/active',
    ORDER: '/study-types/order',
    TOGGLE: (id: string) => `/study-types/${id}/toggle`,
    SUBSCRIBERS: (id: string) => `/study-types/${id}/subscribers`,
  },

  // Study Type Subscriptions
  STUDY_TYPE_SUBSCRIPTIONS: {
    SUMMARY: '/study-type-subscriptions/summary',
    MY: '/users/me/study-type-subscriptions',
    BY_USER: (userId: string) => `/users/${userId}/study-type-subscriptions`,
    SUBSCRIBE: (userId: string) => `/users/${userId}/study-type-subscriptions`,
    UPDATE_ALL: (userId: string) => `/users/${userId}/study-type-subscriptions/update`,
    UNSUBSCRIBE: (userId: string, studyTypeId: string) =>
      `/users/${userId}/study-type-subscriptions/${studyTypeId}/delete`,
  },

  // A que radiologos atiende cada transcriptor. De aqui sale su cola.
  TRANSCRIPTOR_RADIOLOGOS: {
    BY_USER: (userId: string) => `/users/${userId}/radiologos`,
    UPDATE: (userId: string) => `/users/${userId}/radiologos/update`,
  },

  // Study Attachments
  ATTACHMENTS: {
    LIST: (studyId: string) => `/studies/${studyId}/attachments`,
    UPLOAD: (studyId: string) => `/studies/${studyId}/attachments`,
    COUNT: (studyId: string) => `/studies/${studyId}/attachments/count`,
    DOWNLOAD: (studyId: string, attachmentId: string) =>
      `/studies/${studyId}/attachments/${attachmentId}/download`,
    THUMBNAIL: (studyId: string, attachmentId: string) =>
      `/studies/${studyId}/attachments/${attachmentId}/thumbnail`,
    DELETE: (studyId: string, attachmentId: string) =>
      `/studies/${studyId}/attachments/${attachmentId}/delete`,
    TRANSCRIBIR: (studyId: string, attachmentId: string) =>
      `/studies/${studyId}/attachments/${attachmentId}/transcribir`,
  },

  // Report Templates
  REPORT_TEMPLATES: {
    BASE: '/report-templates',
    BY_ID: (id: string) => `/report-templates/${id}`,
    BY_MODALITY: (modalityCode: string) => `/report-templates/by-modality/${modalityCode}`,
    BY_INSTITUTION: (institutionId: string) => `/report-templates/by-institution/${institutionId}`,
    UPDATE: (id: string) => `/report-templates/${id}/update`,
    DELETE: (id: string) => `/report-templates/${id}/delete`,
  },

  // Notifications
  NOTIFICATIONS: {
    BASE: '/notifications',
    UNREAD_COUNT: '/notifications/unread-count',
    MARK_READ: (id: string) => `/notifications/${id}/read`,
    MARK_ALL_READ: '/notifications/read-all',
    DELETE: (id: string) => `/notifications/${id}/delete`,
    DELETE_ALL: '/notifications/delete-all',
  },

  // Dashboard
  DASHBOARD: {
    STATS: '/dashboard/stats',
    STUDIES_BY_MODALITY: '/dashboard/studies-by-modality',
    STUDIES_TIMELINE: '/dashboard/studies-timeline',
    RADIOLOGIST_PRODUCTIVITY: '/dashboard/radiologist-productivity',
    TAT_METRICS: '/dashboard/tat-metrics',
    // Personal endpoints (filtered by userId)
    MY_STATS: '/dashboard/my-stats',
    MY_STUDIES_BY_MODALITY: '/dashboard/my-studies-by-modality',
    MY_PRODUCTIVITY_TIMELINE: '/dashboard/my-productivity-timeline',
    // Global endpoints (SUPER_ADMIN only)
    GLOBAL: {
      STATS: '/dashboard/global/stats',
      STUDIES_BY_MODALITY: '/dashboard/global/studies-by-modality',
      STUDIES_TIMELINE: '/dashboard/global/studies-timeline',
      PRODUCTIVITY: '/dashboard/global/productivity',
      TAT_METRICS: '/dashboard/global/tat-metrics',
    },
  },

  // Hot Folders
  HOT_FOLDERS: {
    BASE: '/hot-folders',
    BY_ID: (id: string) => `/hot-folders/${id}`,
    UPDATE: (id: string) => `/hot-folders/${id}/update`,
    DELETE: (id: string) => `/hot-folders/${id}/delete`,
    TOGGLE: (id: string) => `/hot-folders/${id}/toggle`,
    TEST_CONNECTION: '/hot-folders/test-connection',
    FILES: (id: string) => `/hot-folders/${id}/files`,
    STATS: (id: string) => `/hot-folders/${id}/stats`,
    SCAN: (id: string) => `/hot-folders/${id}/scan`,
    RESET: (id: string) => `/hot-folders/${id}/reset`,
    SCHEDULER_STATUS: '/hot-folders/scheduler/status',
  },

  // Hot Folder Files
  HOT_FOLDER_FILES: {
    PREVIEW: (id: string) => `/hot-folder-files/${id}/preview`,
    PROCESS: (id: string) => `/hot-folder-files/${id}/process`,
    PROCESS_WITH_ATTACHMENTS: (id: string) => `/hot-folder-files/${id}/process-with-attachments`,
    PROCESS_BATCH: '/hot-folder-files/process-batch',
    PROCESS_BATCH_WITH_ATTACHMENTS: '/hot-folder-files/process-batch-with-attachments',
    SKIP: (id: string) => `/hot-folder-files/${id}/skip`,
    OVERRIDE: (id: string) => `/hot-folder-files/${id}/override`,
  },

  // Priorities
  PRIORITIES: {
    BASE: '/priorities',
    BY_ID: (id: string) => `/priorities/${id}`,
    DEFAULT: '/priorities/default',
    UPDATE: (id: string) => `/priorities/${id}/update`,
    DELETE: (id: string) => `/priorities/${id}/delete`,
    SET_DEFAULT: (id: string) => `/priorities/${id}/default`,
  },

  // Appointments
  APPOINTMENTS: {
    BASE: '/appointments',
    BY_ID: (id: string) => `/appointments/${id}`,
    DELETE: (id: string) => `/appointments/${id}/delete`,
    // Status transitions
    SCHEDULE: (id: string) => `/appointments/${id}/schedule`,
    CONFIRM: (id: string) => `/appointments/${id}/confirm`,
    CHECK_IN: (id: string) => `/appointments/${id}/check-in`,
    START: (id: string) => `/appointments/${id}/start`,
    COMPLETE: (id: string) => `/appointments/${id}/complete`,
    CANCEL: (id: string) => `/appointments/${id}/cancel`,
    NO_SHOW: (id: string) => `/appointments/${id}/no-show`,
    RESCHEDULE: (id: string) => `/appointments/${id}/reschedule`,
    // La agenda propia del profesional. Exige `appointment:view:own` y filtra
    // por quien pregunta, no por lo que le permitirian ver.
    MI_AGENDA: '/appointments/mi-agenda',
    // Slots
    SLOTS: '/appointments/slots',
    AVAILABLE_DAYS: '/appointments/available-days',
    // Provider Schedules
    SCHEDULES: {
      // Declarar varios días de una vez. Nadie piensa su agenda por días
      // sueltos: piensa "de lunes a viernes de 8 a 12".
      SEMANA: '/appointments/schedules/semana',
      BASE: '/appointments/schedules',
      BY_ID: (id: string) => `/appointments/schedules/${id}`,
      UPDATE: (id: string) => `/appointments/schedules/${id}/update`,
      DELETE: (id: string) => `/appointments/schedules/${id}/delete`,
    },
    // Schedule Overrides
    OVERRIDES: {
      BASE: '/appointments/overrides',
      DELETE: (id: string) => `/appointments/overrides/${id}/delete`,
    },
  },
} as const

// =============================================================================
// Application Routes
// =============================================================================

export const APP_ROUTES = {
  // Public
  LOGIN: '/login',
  PATIENT_LOGIN: '/pacientes/login',
  REGISTER: '/register',
  FORGOT_PASSWORD: '/forgot-password',
  CHANGE_PASSWORD: '/change-password',

  // Dashboard
  DASHBOARD: '/dashboard',

  // Patients
  PATIENTS: '/patients',
  PATIENT_DETAIL: (id: string) => `/patients/${id}`,
  PATIENT_NEW: '/patients/new',

  // Studies
  STUDIES: '/studies',
  STUDY_DETAIL: (id: string) => `/studies/${id}`,
  STUDY_VIEWER: (id: string) => `/viewer/${id}`,

  // Worklist
  WORKLIST: '/worklist',

  // Reports
  REPORTS: '/reports',
  REPORT_DETAIL: (id: string) => `/reports/${id}`,
  REPORT_NEW: (studyId: string) => `/reports/new?studyId=${studyId}`,

  // Transcription (for TRANSCRIPTOR role)
  TRANSCRIPTION: {
    QUEUE: '/transcription',
    WORK: (id: string) => `/transcription/${id}`,
  },

  // Admin
  ADMIN: {
    USERS: '/admin/users',
    ROLES: '/admin/roles',
    AUDIT: '/admin/audit',
    SETTINGS: '/admin/settings',
    // Dónde atiende la institución y con qué equipos. Es lo que determina
    // cuántos pacientes caben a la misma hora.
    SEDES: '/admin/sedes',
    STUDY_TYPES: '/admin/study-types',
    TEMPLATES: '/admin/templates',
    HOT_FOLDERS: '/admin/hot-folders',
    HOT_FOLDER_FILES: (id: string) => `/admin/hot-folders/${id}/files`,
    PRIORITIES: '/admin/priorities',
  },

  // Super Admin
  SUPER_ADMIN: {
    DASHBOARD: '/super-admin',
    INSTITUTIONS: '/super-admin/institutions',
    INSTITUTION_DETAIL: (id: string) => `/super-admin/institutions/${id}`,
    PLANS: '/super-admin/plans',
    CATALOG: '/super-admin/catalog',
    INSTITUTION_NEW: '/super-admin/institutions/new',
    SUBSCRIPTIONS: '/super-admin/subscriptions',
    AUDIT: '/super-admin/audit',
    SYSTEM: '/super-admin/system',
  },

  // Appointments
  APPOINTMENTS: '/appointments',
  MI_AGENDA: '/mi-agenda',
  APPOINTMENT_DETAIL: (id: string) => `/appointments/${id}`,
  APPOINTMENT_NEW: '/appointments/new',
  MY_AGENDA: '/appointments/my-agenda',

  // Admin: Schedules
  ADMIN_SCHEDULES: '/admin/schedules',

  // Patient Portal
  PATIENT_PORTAL: {
    HOME: '/mi-portal',
    MY_STUDIES: '/mi-portal/estudios',
    STUDY_DETAIL: (id: string) => `/mi-portal/estudios/${id}`,
    VIEWER: (id: string) => `/mi-portal/visor/${id}`,
    APPOINTMENTS: '/mi-portal/citas',
    APPOINTMENT_NEW: '/mi-portal/citas/nueva',
    APPOINTMENT_DETAIL: (id: string) => `/mi-portal/citas/${id}`,
  },

  // Profile
  PROFILE: '/profile',
  SETTINGS: '/settings',
} as const

// =============================================================================
// Pagination
// =============================================================================

export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 20,
  MAX_LIMIT: 100,
  PAGE_SIZE_OPTIONS: [10, 20, 50, 100],
} as const

// =============================================================================
// Tipos de Estudio — catálogo de nombres en español para códigos DICOM internos
// Fuente de verdad: esta tabla. Nunca mostrar el código técnico al usuario.
// =============================================================================

export const STUDY_TYPE_NAMES: Record<string, string> = {
  CR: 'Radiografía Computarizada',
  CT: 'Tomografía Computarizada',
  DX: 'Radiografía Digital',
  DR: 'Radiografía Digital',
  MG: 'Mamografía',
  MR: 'Resonancia Magnética',
  NM: 'Medicina Nuclear',
  OT: 'Otro',
  PT: 'PET',
  RF: 'Fluoroscopia',
  US: 'Ecografía',
  XA: 'Angiografía',
  ES: 'Endoscopía',
  SC: 'Captura Secundaria',
  IO: 'Intraoral',
  PX: 'Panorámica',
} as const

/**
 * Retorna el nombre en español del tipo de estudio.
 * Si viene de la BD, usar `studyType.name` directamente (es preferible).
 * Esta función es fallback para cuando solo se tiene el código DICOM.
 */
export const getStudyTypeName = (code: string | null | undefined): string => {
  if (!code) return 'N/A'
  return STUDY_TYPE_NAMES[code.toUpperCase()] ?? code
}

// =============================================================================
// Date/Time Formats
// =============================================================================

export const DATE_FORMATS = {
  DATE: 'dd/MM/yyyy',
  TIME: 'HH:mm',
  DATETIME: 'dd/MM/yyyy HH:mm',
  DATETIME_FULL: 'dd/MM/yyyy HH:mm:ss',
  ISO: "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
} as const

// =============================================================================
// Validation
// =============================================================================

export const VALIDATION = {
  PASSWORD_MIN_LENGTH: 8,
  PASSWORD_MAX_LENGTH: 128,
  NAME_MIN_LENGTH: 2,
  NAME_MAX_LENGTH: 100,
  EMAIL_MAX_LENGTH: 255,
  PHONE_PATTERN: /^\+?[0-9]{7,15}$/,
} as const

// =============================================================================
// User Roles (sync with backend)
// =============================================================================

export const USER_ROLES = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  ADMIN: 'ADMIN',
  RADIOLOGIST: 'RADIOLOGIST',
  DOCTOR: 'DOCTOR',
  PATIENT: 'PATIENT',
  TECHNICIAN: 'TECHNICIAN',
  TRANSCRIPTOR: 'TRANSCRIPTOR',
} as const

export type UserRole = (typeof USER_ROLES)[keyof typeof USER_ROLES]

// =============================================================================
// Document Types (Colombian)
// =============================================================================

export const DOCUMENT_TYPES = {
  CC: 'CC',   // Cédula de Ciudadanía
  TI: 'TI',   // Tarjeta de Identidad
  CE: 'CE',   // Cédula de Extranjería
  PA: 'PA',   // Pasaporte
  RC: 'RC',   // Registro Civil
  NIT: 'NIT', // NIT (empresas)
  MS: 'MS',   // Menor sin Identificación
  AS: 'AS',   // Adulto sin Identificación
  CD: 'CD',   // Carné Diplomático
  SC: 'SC',   // Salvoconducto
  PE: 'PE',   // Permiso Especial de Permanencia
  NV: 'NV',   // Nacido Vivo
} as const

export type DocumentType = (typeof DOCUMENT_TYPES)[keyof typeof DOCUMENT_TYPES]

export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  CC: 'Cédula de Ciudadanía',
  TI: 'Tarjeta de Identidad',
  CE: 'Cédula de Extranjería',
  PA: 'Pasaporte',
  RC: 'Registro Civil',
  NIT: 'NIT',
  MS: 'Menor sin Identificación',
  AS: 'Adulto sin Identificación',
  CD: 'Carné Diplomático',
  SC: 'Salvoconducto',
  PE: 'Permiso Especial',
  NV: 'Nacido Vivo',
}

// =============================================================================
// Códigos DICOM internos — solo para interoperabilidad técnica, nunca mostrar al usuario
// =============================================================================

export const DICOM_CODES = {
  DX: 'DX',
  CT: 'CT',
  MR: 'MR',
  US: 'US',
  MG: 'MG',
  XA: 'XA',
  NM: 'NM',
  PT: 'PT',
  RF: 'RF',
  OT: 'OT',
} as const

export type DicomCode = (typeof DICOM_CODES)[keyof typeof DICOM_CODES]

// Aliases de compatibilidad — los componentes los usan para mostrar el nombre
// del tipo de estudio a partir del código DICOM almacenado en studies.modality
export type Modality = DicomCode
export const MODALITY_LABELS = STUDY_TYPE_NAMES as Record<string, string>
export const getModalityDisplayName = getStudyTypeName

// =============================================================================
// Attachment Types
// =============================================================================

export const ATTACHMENT_TYPES = {
  CLINICAL_HISTORY: 'CLINICAL_HISTORY',
  MEDICAL_ORDER: 'MEDICAL_ORDER',
  PREVIOUS_RESULTS: 'PREVIOUS_RESULTS',
  ADDITIONAL_IMAGES: 'ADDITIONAL_IMAGES',
  DICTATION_AUDIO: 'DICTATION_AUDIO',
  OTHER: 'OTHER',
} as const

export type AttachmentType = (typeof ATTACHMENT_TYPES)[keyof typeof ATTACHMENT_TYPES]

export const ATTACHMENT_TYPE_LABELS: Record<AttachmentType, string> = {
  CLINICAL_HISTORY: 'Historia Clínica',
  MEDICAL_ORDER: 'Orden Médica',
  PREVIOUS_RESULTS: 'Resultados Previos',
  ADDITIONAL_IMAGES: 'Imágenes Adicionales',
  DICTATION_AUDIO: 'Dictado grabado',
  OTHER: 'Otro',
}

// =============================================================================
// BIRADS Categories (Breast Imaging Reporting and Data System)
// =============================================================================

export const BIRADS_CATEGORIES = {
  '0': '0',
  '1': '1',
  '2': '2',
  '3': '3',
  '4A': '4A',
  '4B': '4B',
  '4C': '4C',
  '5': '5',
  '6': '6',
} as const

export type BiradsCategory = (typeof BIRADS_CATEGORIES)[keyof typeof BIRADS_CATEGORIES]

export const BIRADS_LABELS: Record<BiradsCategory, string> = {
  '0': '0 - Incompleto, requiere evaluación adicional',
  '1': '1 - Negativo',
  '2': '2 - Hallazgo benigno',
  '3': '3 - Probablemente benigno',
  '4A': '4A - Baja sospecha de malignidad',
  '4B': '4B - Sospecha intermedia de malignidad',
  '4C': '4C - Sospecha moderada de malignidad',
  '5': '5 - Alta sospecha de malignidad',
  '6': '6 - Malignidad comprobada por biopsia',
}

export const BIRADS_SHORT_LABELS: Record<BiradsCategory, string> = {
  '0': 'BIRADS 0 - Incompleto',
  '1': 'BIRADS 1 - Negativo',
  '2': 'BIRADS 2 - Benigno',
  '3': 'BIRADS 3 - Prob. benigno',
  '4A': 'BIRADS 4A - Baja sospecha',
  '4B': 'BIRADS 4B - Sospecha intermedia',
  '4C': 'BIRADS 4C - Sospecha moderada',
  '5': 'BIRADS 5 - Alta sospecha',
  '6': 'BIRADS 6 - Malignidad confirmada',
}

// =============================================================================
// Report Status
// =============================================================================

export const REPORT_STATUS = {
  DRAFT: 'DRAFT',
  TRANSCRIBING: 'TRANSCRIBING',
  REVIEW: 'REVIEW',
  PRELIMINARY: 'PRELIMINARY',
  FINAL: 'FINAL',
  AMENDED: 'AMENDED',
  CANCELLED: 'CANCELLED',
} as const

export type ReportStatus = (typeof REPORT_STATUS)[keyof typeof REPORT_STATUS]

export const REPORT_STATUS_LABELS: Record<ReportStatus, string> = {
  DRAFT: 'Borrador',
  TRANSCRIBING: 'En Transcripción',
  REVIEW: 'En Revisión',
  PRELIMINARY: 'Por Firmar',
  FINAL: 'Firmado',
  AMENDED: 'Corregido',
  CANCELLED: 'Cancelado',
}

// =============================================================================
// Worklist Status
// =============================================================================

export const WORKLIST_STATUS = {
  PENDING: 'PENDING',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
  RETURNED: 'RETURNED',
} as const

export type WorklistStatus = (typeof WORKLIST_STATUS)[keyof typeof WORKLIST_STATUS]

export const WORKLIST_STATUS_LABELS: Record<WorklistStatus, string> = {
  PENDING: 'Pendiente',
  IN_PROGRESS: 'En Lectura',
  COMPLETED: 'Leído',
  CANCELLED: '', // No usar - legacy
  RETURNED: 'Devuelto',
}

// =============================================================================
// Priority Levels
// =============================================================================

export const PRIORITY_LEVELS = {
  1: 'STAT',      // Emergency
  2: 'URGENT',    // Urgent
  3: 'ROUTINE',   // Routine
  4: 'LOW',       // Low priority
} as const

export type PriorityLevel = keyof typeof PRIORITY_LEVELS

export const PRIORITY_LABELS: Record<PriorityLevel, string> = {
  1: 'STAT (Emergencia)',
  2: 'Urgente',
  3: 'Rutina',
  4: 'Baja',
}

export const PRIORITY_COLORS: Record<PriorityLevel, string> = {
  1: 'destructive',
  2: 'warning',
  3: 'default',
  4: 'secondary',
}
